import { StrictMode, useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router";

import { AppRoutes, type Session } from "./app/Routes";
import { api, type User } from "./lib/api";
import { refreshServiceWorker } from "./lib/push";
import { useLivenessOnWake, useWakeUp, type ShellCounts } from "./lib/wakeup";
import { Reconnecting } from "./screens/Reconnecting";
import { initI18n, setLocale } from "./i18n";

/**
 * How long the reconnecting screen waits before each re-ask (decision 887):
 * quickly at first, for the dropped packet, then backing off so a struggling
 * server is not asked every few seconds by every open copy of the app.
 */
const RECONNECT_DELAYS_MS = [3_000, 10_000, 30_000, 60_000];

import "./styles/tokens.css";
import "./styles/base.css";

// i18n before first paint: every component reads its strings from it (direction
// §7 bars literals in components), so rendering first would flash raw keys.
//
// NO ARGUMENT MEANS "FOLLOW THE DEVICE" (decision 537). The account's stored
// choice is not known until /api/me answers, and blocking first paint on that
// would make a dead connection a blank app — which the catch below deliberately
// prevents. So the app boots in the device's language, which is right for a
// signed-out visitor and for most signed-in ones, and `applyPreferences`
// switches when a different stored preference arrives.
await initI18n();

// See refreshServiceWorker: a device already enrolled in push may go days
// without a navigation inside scope, and a push event alone never triggers a
// worker update. Fire-and-forget and a no-op for a reader who never enrolled.
void refreshServiceWorker();

/**
 * The session boundary.
 *
 * `/api/me` is the one public read, answering 200 with `user: null` rather than
 * 401, because it is the call the app makes BEFORE it knows whether it has a
 * session — so "nobody" has to be an answer rather than a failure.
 *
 * The theme is applied from the server-held preference (contract §2) rather than
 * from `prefers-color-scheme` alone. tokens.css keys off `[data-theme]` first and
 * falls back to the media query, so "system" means removing the attribute rather
 * than computing a value here — one place decides, and the brand's mode pair
 * cannot disagree with the palette.
 *
 * IT ALSO HOLDS THE SHELL'S FACTS, which are shell state rather than screen
 * state: the Active tab's existence depends on them (decisions 324, 546) and the
 * shell wraps every screen, so no single route can own them. `/api/me` seeds
 * them on a cold load — including a reload straight onto /past, where nothing
 * else would ask — and the wake-up check is what refreshes them (384).
 *
 * THE LANDING DECISION REFRESHES NOTHING (decision 503). It fetches /api/active,
 * which is view-scoped and defaults to the primary list, so a count taken from
 * it would hide the tab whenever the live event sat on a list the reader was not
 * viewing — the defect that took the tab away on a live weekend.
 */
function Root() {
  const [session, setSession] = useState<Session | null>(null);
  // Set while the cold load has failed and no session is known (decision 887).
  const [unreachable, setUnreachable] = useState(false);
  const [trying, setTrying] = useState(false);
  const attempts = useRef(0);
  const timer = useRef<number | undefined>(undefined);
  const mounted = useRef(true);

  /*
   * A FAILED COLD LOAD IS NOT A SIGNED-OUT ONE (decision 887). This used to
   * render a failure as nobody, which put a coach with a good session on the
   * sign-in screen whenever the server was slow — on 25 Oct, for an hour, and
   * each sign-in they then tried waited on the same slow server. Now a failure
   * shows the reconnecting screen and re-asks on a backoff, and the server's
   * own 200 with `user: null` stays the only way to reach sign-in.
   */
  const load = useCallback(function load() {
    window.clearTimeout(timer.current);
    setTrying(true);
    api
      .me()
      .then(
        ({
          user,
          activeEventCount,
          activeFencerCount,
          lastFencerDoneAt,
          unreadAlertCount,
          serverVersion,
          serverCommit,
          pushPublicKey,
          features,
          listMemberCap,
          listCountCap,
        }) => {
          if (!mounted.current) return;
          setSession({
            user,
            activeEventCount,
            activeFencerCount,
            lastFencerDoneAt,
            unreadAlertCount,
            serverVersion,
            serverCommit,
            pushPublicKey,
            features,
            ...(listMemberCap !== undefined ? { listMemberCap } : {}),
            ...(listCountCap !== undefined ? { listCountCap } : {}),
          });
          setUnreachable(false);
          setTrying(false);
          applyPreferences(user);
        },
      )
      .catch(() => {
        if (!mounted.current) return;
        setUnreachable(true);
        setTrying(false);
        const delay = RECONNECT_DELAYS_MS[Math.min(attempts.current, RECONNECT_DELAYS_MS.length - 1)];
        attempts.current += 1;
        timer.current = window.setTimeout(load, delay);
      });
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
      window.clearTimeout(timer.current);
    };
  }, [load]);

  // Coming back to the app while unreachable re-asks at once rather than
  // waiting out the backoff; with a session, the wake-up check below owns it.
  useWakeUp(
    useCallback(() => {
      if (unreachable && !trying) load();
    }, [unreachable, trying, load]),
  );

  // Applied whenever the user changes, not only on first load: switching the
  // theme in Settings must repaint immediately rather than after a reload.
  //
  // The shell's facts are CARRIED THROUGH rather than reset, because this fires
  // on a preference edit as well as on sign-in, and blanking them would make the
  // Active tab vanish when someone flips the theme. A fresh sign-in starts from
  // the previous values until the next /api/me — the sign-in response itself, or
  // the wake-up check — replaces them.
  const onUserChanged = (user: User | null) => {
    setSession((prev) => ({
      user,
      activeEventCount: prev?.activeEventCount ?? 0,
      activeFencerCount: prev?.activeFencerCount ?? 0,
      ...(prev?.lastFencerDoneAt ? { lastFencerDoneAt: prev.lastFencerDoneAt } : {}),
      unreadAlertCount: prev?.unreadAlertCount ?? 0,
      ...(prev?.serverVersion ? { serverVersion: prev.serverVersion } : {}),
      ...(prev?.serverCommit ? { serverCommit: prev.serverCommit } : {}),
      // Carried through for the reason the counts are: this fires on a
      // preference edit too, and it is a property of the deployment rather than
      // of the session, so dropping it would take the enrolment control away
      // from someone who changed their theme.
      ...(prev?.pushPublicKey ? { pushPublicKey: prev.pushPublicKey } : {}),
      // Carried through for the same reason: a grant is moved by an admin, not
      // by anything a preference edit or a sign-in does, and dropping it here
      // would make the list chrome disappear when somebody changed their theme.
      features: prev?.features ?? [],
      // A deployment fact, carried through as pushPublicKey is (decision 889).
      ...(prev?.listMemberCap !== undefined ? { listMemberCap: prev.listMemberCap } : {}),
      ...(prev?.listCountCap !== undefined ? { listCountCap: prev.listCountCap } : {}),
    }));
    applyPreferences(user);
  };

  // Reported by the wake-up check, and by the feed screen after it advances the
  // watermark — so the badge clears as the reader looks at the list rather than
  // on the next cold load.
  const onUnreadCountChanged = useCallback((unreadAlertCount: number) => {
    setSession((prev) => (prev ? { ...prev, unreadAlertCount } : prev));
  }, []);

  // Both shell counts arrive from one /api/me, so one callback sets both rather
  // than two closures racing to patch the same object.
  const onShellCounts = useCallback((counts: ShellCounts) => {
    setSession((prev) => (prev ? { ...prev, ...counts } : prev));
  }, []);

  // NOTHING REPORTS A TRACKED-FENCER COUNT HERE ANY MORE (decision 501). The
  // roster screen used to hand one up so the fencer-axis collapse (359) tracked
  // an edit without a reload; the collapse is now computed from each view's own
  // payload, which the view re-fetches anyway, so the count has no home in
  // session state and the callback had nowhere left to write.

  /*
   * COMING BACK TO THE APP re-asks whether anything is live (roadmap stage 2
   * item 4), so the Active tab can appear without a reload. The rule — and in
   * particular that a failed re-ask must never zero the count (decision 324) —
   * lives in lib/wakeup.ts; this is only the wiring to shell state.
   *
   * Each VIEW refreshes its own payload on its own staleness threshold, in
   * Routes.tsx. Kept apart because they answer to different numbers: the chrome
   * always re-asks, a view only when what it holds has aged past its regime.
   */
  useLivenessOnWake(onShellCounts);

  if (!session) return unreachable ? <Reconnecting trying={trying} onRetry={load} /> : null;

  return (
    <BrowserRouter>
      <AppRoutes
        session={session}
        onUserChanged={onUserChanged}
        onUnreadCountChanged={onUnreadCountChanged}
      />
    </BrowserRouter>
  );
}

function applyPreferences(user: User | null): void {
  const root = document.documentElement;

  const theme = user?.theme ?? "system";
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);

  // The in-app motion toggle, which §6 requires to be honoured ALONGSIDE the OS
  // setting rather than instead of it. As an attribute so CSS can express "either
  // one suppresses motion" in one place instead of every animated rule asking
  // twice.
  if (user?.reduce_motion) root.setAttribute("data-reduce-motion", "true");
  else root.removeAttribute("data-reduce-motion");

  /*
   * THE LANGUAGE, applied here with the theme because it is the same kind of
   * thing: a server-held preference that has to take effect without a reload
   * (decision 537). Changing it in Settings re-renders in that language,
   * because `changeLanguage` is what every `useTranslation` subscribes to.
   *
   * `ui_locale` ABSENT means follow the device, which is what a signed-out
   * visitor already got at boot — so this is a no-op for them rather than a
   * reset. Fire-and-forget: a catalogue that will not load leaves English in
   * place, and nothing on this path is worth failing a sign-in over.
   */
  void setLocale(user?.ui_locale ?? null);
}

const root = document.getElementById("root");
if (!root) throw new Error("no #root element");

createRoot(root).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
